/* eslint-disable import/no-extraneous-dependencies  */
import { EventType, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { Router } from '@src/app/components/Router';
import { GlobalStyles as Styles } from '@src/app/styles/GlobalStyles';
import { msalConfig } from '@src/shared/configuration/authentication';
import themeConfiguration from '@src/shared/configuration/theme.json';
import { logger } from '@src/shared/includes/logger';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { config } from '@uhg-abyss/web/tools/config';
import { createTheme } from '@uhg-abyss/web/tools/theme';
import { AbyssProvider } from '@uhg-abyss/web/ui/AbyssProvider';
import { ToastProvider } from '@uhg-abyss/web/ui/Toast';
import Head from 'next/head';
import React from 'react';

export const AbyssTheme = createTheme('uhg', themeConfiguration);

export const msalInstance = new PublicClientApplication(msalConfig);

msalInstance.initialize().then(() => {
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  const accounts = msalInstance.getAllAccounts();

  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  /**
   * Set active account.
   */
  msalInstance.addEventCallback((event) => {
    if (event?.eventType === EventType?.LOGIN_SUCCESS && event?.payload?.account) {
      msalInstance.setActiveAccount(event?.payload?.account);
    }
  });

  /**
   * Log authentication events.
   */
  msalInstance.addEventCallback((event) => {
    if (event?.eventType === EventType?.LOGIN_START) {
      logger.log('[Event] -> Authentication: Login started.', true);
    }

    if (event?.eventType === EventType?.LOGIN_SUCCESS && event?.payload?.account) {
      logger.log('[Event] -> Authentication: Login succeeded.', true, event?.payload?.account?.username);
    }

    if (event?.eventType === EventType?.LOGIN_FAILURE && event?.payload?.account) {
      logger.log('[Event] -> Authentication: Login failed.', true, event?.payload?.account?.username);
    }

    if (event?.eventType === EventType?.LOGOUT_START && event?.payload?.account) {
      logger.log('[Event] -> Authentication: Logout started.', true, event?.payload?.account?.username);
    }

    if (event?.eventType === EventType?.LOGOUT_SUCCESS && event?.payload?.account) {
      logger.log('[Event] -> Authentication: Logout succeeded.', true, event?.payload?.account?.username);
    }

    if (event?.eventType === EventType?.LOGOUT_FAILURE && event?.payload?.account) {
      logger.log('[Event] -> Authentication: Logout failed.', true, event?.payload?.account?.username);
    }

    if (event?.eventType === EventType?.LOGOUT_END && event?.payload?.account) {
      logger.log('[Event] -> Authentication: Logout ended.', true, event?.payload?.account?.username);
    }
  });
});

/**
 * browser
 *
 * The main file responsible to handle global application logic.
 *
 * @returns {JSX.Element}
 */
export const browser = () => {
  return (
    <ErrorHandler location="src/app/browser.jsx">
      <MsalProvider instance={msalInstance}>
        <AbyssProvider theme={AbyssTheme}>
          <Styles theme={AbyssTheme}>
            <ToastProvider />
            <Head>
              <title>
                {config('APP_ENV') !== 'prod' ? `${config('APP_NAME')} | ${config('APP_ENV')}` : config('APP_NAME')}
              </title>
            </Head>
            <Router />
          </Styles>
        </AbyssProvider>
      </MsalProvider>
    </ErrorHandler>
  );
};
